'use client';

import { ssrTrack } from '@/lib/actions/analytics/ssr-track';
import { EvType } from '@/lib/actions/usage/constants';
import {
    getDatasetUtilityName,
    getSunrunUtilityName,
} from '@/lib/utilities/utilityMapping';
import { useAtom } from 'jotai';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import {
    addCleanHeatAndAcAtom,
    addHeatPumpWaterHeaterAtom,
    addHvacUseAtom,
    addInductionStoveAtom,
    addPeopleAtom,
    baseUsageAtom,
    changeAddPeopleAtom,
    electricHeatAtom,
    Ev,
    evsAtom,
    flexAtom,
    hasAcAtom,
    hasElectricStoveAtom,
    hasElectricWaterHeaterAtom,
    hasHeatPumpAtom,
    hhSizeAtom,
    sqftAtom,
    storiesAtom,
    utilityCoAtom,
    yearBuiltAtom,
    zipCodeAtom,
} from '../atoms/form-atoms';

// Don't let the reader and writer interfere with each other.
export const enum Stage {
    Initial,
    Initializing,
    Done,
}

export function inputToSearchParams({
    flex,
    baseUsage,
    zipCode,
    yearBuilt,
    sqft,
    stories,
    hhSize,
    utilityCo,
    hasAc,
    electricHeat,
    hasHeatPump,
    evs,
    changeAddPeople,
    addPeople,
    hasElectricStove,
    addHvacUse,
    addCleanHeatAndAc,
    addElectricWaterHeater,
    addInductionStove,
    addHeatPumpWaterHeater,
}: {
    flex: boolean;
    baseUsage: string;
    zipCode: string;
    yearBuilt: string;
    sqft: string;
    hhSize: string;

    utilityCo: string;
    hasAc: boolean;
    electricHeat: boolean;
    hasHeatPump: boolean;
    evs: Ev[];
    stories: string;
    changeAddPeople: boolean;
    addPeople: string;
    addHvacUse: boolean;
    addCleanHeatAndAc: boolean;
    addInductionStove: boolean;
    hasElectricStove: boolean;
    addHeatPumpWaterHeater: boolean;
    addElectricWaterHeater: boolean;
}): URLSearchParams {
    const urlParams = new URLSearchParams();

    function setBool(paramName: string, b: boolean) {
        urlParams.set(paramName, b ? '1' : '0');
    }

    function setString(paramName: string, v: string | undefined) {
        urlParams.set(paramName, v ?? '');
    }

    function setEv(index: number, ev: Ev) {
        setString(`ev${index}Type`, ev.type);
        setString(`ev${index}MilesPerYear`, ev.milesPerYear);
        setString(`ev${index}HomeChgPct`, ev.homeChgPct);
    }

    setBool('flex', flex);
    setString('baseUsage', baseUsage);
    setString('zip', zipCode);
    setString('yearBuilt', yearBuilt);
    setString('sqft', sqft);
    setString('hhSize', hhSize);

    // Search params uses the Sunrun utility name.
    setString('utilityCo', getSunrunUtilityName(utilityCo));

    setBool('hasAc', hasAc);
    setBool('electricHeat', electricHeat);
    setBool('hasHeatPump', hasHeatPump);
    setBool('hasElectricStove', hasElectricStove);
    setBool('addElectricWaterHeater', addElectricWaterHeater);

    const emptyEv = { type: undefined, milesPerYear: '', homeChgPct: '' };
    setEv(1, evs[0] ?? emptyEv);
    setEv(2, evs[1] ?? emptyEv);

    setBool('changeAddPeople', changeAddPeople);
    setString('addPeople', addPeople);
    setBool('addHvacUse', addHvacUse);
    setString('stories', stories);

    setBool('addCleanHeatAndAc', addCleanHeatAndAc);
    setBool('addInductionStove', addInductionStove);
    setBool('addHeatPumpWaterHeater', addHeatPumpWaterHeater);

    return urlParams;
}

export function useAutoUpdateSearchParams(): Stage {
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();

    const [stage, setStage] = useState(Stage.Initial);

    const [flex, setFlex] = useAtom(flexAtom);
    const [baseUsage, setBaseUsage] = useAtom(baseUsageAtom);
    const [zipCode, setZipCode] = useAtom(zipCodeAtom);
    const [yearBuilt, setYearBuilt] = useAtom(yearBuiltAtom);
    const [sqft, setSqft] = useAtom(sqftAtom);
    const [hhSize, setHhSize] = useAtom(hhSizeAtom);
    const [utilityCo, setUtilityCo] = useAtom(utilityCoAtom);

    const [hasAc, setHasAc] = useAtom(hasAcAtom);
    const [electricHeat, setElectricHeat] = useAtom(electricHeatAtom);
    const [hasHeatPump, setHasHeatPump] = useAtom(hasHeatPumpAtom);
    const [hasElectricStove, setHasElectricStove] =
        useAtom(hasElectricStoveAtom);
    const [addElectricWaterHeater, setAddElectricWaterHeater] = useAtom(
        hasElectricWaterHeaterAtom
    );
    const [stories, setStories] = useAtom(storiesAtom);

    const [addPeople, setAddPeople] = useAtom(addPeopleAtom);
    const [addHvacUse, setAddHvacUse] = useAtom(addHvacUseAtom);
    const [addCleanHeatAndAc, setAddCleanHeatAndAc] = useAtom(
        addCleanHeatAndAcAtom
    );

    const [addHeatPumpWaterHeater, setAddHeatPumpWaterHeater] = useAtom(
        addHeatPumpWaterHeaterAtom
    );
    const [evs, setEvs] = useAtom(evsAtom);

    const [addInductionStove, setAddInductionStove] = useAtom(
        addInductionStoveAtom
    );
    const [changeAddPeople, setChangeAddPeople] = useAtom(changeAddPeopleAtom);

    const setBooleanParam = useCallback(
        (paramName: string, setter: (b: boolean) => void) => {
            const paramValue = searchParams.get(paramName);

            if (paramValue !== null) {
                const value = paramValue === '1';
                setter(value);
                return true;
            }

            return false;
        },
        [searchParams]
    );

    const setNumberParam = useCallback(
        // All numbers are represented as string type so that we can get the
        // empty value. If we use number, we can only get "0", which is not the
        // same thing as an empty string.
        (paramName: string, setter: (s: string) => void) => {
            const paramValue = searchParams.get(paramName);

            if (paramValue !== null) {
                if (!paramValue) {
                    setter('');
                    return false;
                }
                const value = Number(paramValue);
                if (!isNaN(value)) {
                    setter(String(value));
                    return value ? true : false;
                }
            }

            return false;
        },
        [searchParams]
    );

    const setStringParam = useCallback(
        (paramName: string, setter: (s: string) => void) => {
            const paramValue = searchParams.get(paramName);

            if (paramValue !== null) {
                setter(paramValue);
                return paramValue ? true : false;
            }

            return false;
        },
        [searchParams]
    );

    // Fetch params from url and set them.
    useEffect(() => {
        if (stage !== Stage.Initial) return;
        setStage(Stage.Initializing);

        setBooleanParam('flex', setFlex);
        setNumberParam('baseUsage', setBaseUsage);
        setStringParam('zip', setZipCode);
        setNumberParam('yearBuilt', setYearBuilt);
        setNumberParam('sqft', setSqft);
        setNumberParam('hhSize', setHhSize);

        // The internal value used is the dataset utility name.
        setStringParam('utilityCo', (s: string) =>
            setUtilityCo(getDatasetUtilityName(s))
        );

        setNumberParam('stories', setStories);
        setBooleanParam('hasAc', setHasAc);
        setBooleanParam('electricHeat', setElectricHeat);
        setBooleanParam('hasHeatPump', setHasHeatPump);
        setBooleanParam('hasElectricStove', setHasElectricStove);
        setBooleanParam('addElectricWaterHeater', setAddElectricWaterHeater);
        setBooleanParam('addHvacUse', setAddHvacUse);
        setBooleanParam('addCleanHeatAndAc', setAddCleanHeatAndAc);
        setBooleanParam('addHeatPumpWaterHeater', setAddHeatPumpWaterHeater);
        setBooleanParam('addInductionStove', setAddInductionStove);

        const evsArray: Ev[] = [
            { type: undefined, milesPerYear: '', homeChgPct: '' },
            { type: undefined, milesPerYear: '', homeChgPct: '' },
        ];

        let hasEv1 = setStringParam(
            'ev1Type',
            (v) => (evsArray[0].type = v as EvType)
        );
        // Warning: The `|| hasEv1` part must be placed at the end, else
        // `setNumberParam` won't be run at all.
        hasEv1 =
            setNumberParam(
                'ev1MilesPerYear',
                (v) => (evsArray[0].milesPerYear = v)
            ) || hasEv1;
        hasEv1 =
            setNumberParam(
                'ev1HomeChgPct',
                (v) => (evsArray[0].homeChgPct = v)
            ) || hasEv1;

        let hasEv2 = setStringParam(
            'ev2Type',
            (v) => (evsArray[1].type = v as EvType)
        );
        hasEv2 =
            setNumberParam(
                'ev2MilesPerYear',
                (v) => (evsArray[1].milesPerYear = v)
            ) || hasEv2;
        hasEv2 =
            setNumberParam(
                'ev2HomeChgPct',
                (v) => (evsArray[1].homeChgPct = v)
            ) || hasEv2;

        if (hasEv1 || hasEv2) {
            if (!hasEv2) evsArray.pop();
            setEvs(evsArray);
        }

        // Warning: changeAddPeople will auto-update addPeople, so we need to
        // set addPeople first.
        setStringParam('addPeople', setAddPeople);
        setBooleanParam('changeAddPeople', setChangeAddPeople);
        setBooleanParam('addHvacUse', setAddHvacUse);
        setBooleanParam('addCleanHeatAndAc', setAddCleanHeatAndAc);
        setBooleanParam('addHeatPumpWaterHeater', setAddHeatPumpWaterHeater);
        setBooleanParam('addInductionStove', setAddInductionStove);
        setBooleanParam('hasElectricStove', setHasElectricStove);
        setBooleanParam('addElectricWaterHeater', setAddElectricWaterHeater);

        setStage(Stage.Done);
    }, [
        setAddCleanHeatAndAc,
        setAddElectricWaterHeater,
        setAddHeatPumpWaterHeater,
        setAddHvacUse,
        setAddInductionStove,
        setAddPeople,
        setBaseUsage,
        setBooleanParam,
        setChangeAddPeople,
        setElectricHeat,
        setEvs,
        setFlex,
        setHasAc,
        setHasElectricStove,
        setHasHeatPump,
        setHhSize,
        setNumberParam,
        setSqft,
        setStories,
        setStringParam,
        setUtilityCo,
        setYearBuilt,
        setZipCode,
        stage,
    ]);

    // Update the url param using the current values.
    useEffect(() => {
        if (stage !== Stage.Done) return;

        const urlParams = inputToSearchParams({
            flex,
            baseUsage,
            zipCode,
            yearBuilt,
            sqft,
            hhSize,
            utilityCo,
            hasAc,
            electricHeat,
            hasHeatPump,
            stories,
            evs,
            changeAddPeople,
            addPeople,
            addHvacUse,
            addCleanHeatAndAc,
            addHeatPumpWaterHeater,
            addInductionStove,
            hasElectricStove,
            addElectricWaterHeater,
        });

        const newUrl = `${pathname}?${urlParams.toString()}`;

        // Important to not disrupt the user by unnecessary scrolling.
        router.replace(newUrl, { scroll: false });

        // Override the `$current_url` from window.location.href which is the
        // old url before it becomes newUrl.
        ssrTrack('updateInputs', { $current_url: newUrl });
    }, [
        addHvacUse,
        addPeople,
        baseUsage,
        changeAddPeople,
        electricHeat,
        evs,
        flex,
        hasAc,
        hasHeatPump,
        hhSize,
        pathname,
        router,
        stories,
        sqft,
        stage,
        zipCode,
        utilityCo,
        yearBuilt,
        addCleanHeatAndAc,
        addHeatPumpWaterHeater,
        addInductionStove,
        hasElectricStove,
        addElectricWaterHeater,
    ]);

    return stage;
}
